import { Component, OnInit, inject } from '@angular/core';
import { StateService } from '@uirouter/core';
import { LanguageMatchRepoService } from '@client/app/services/api/language-match-repo/language-match-repo.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { SessionService } from '@monsido/core/session/session.service';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';
import { LanguageDisplayPipe } from '../pipes/language-display.pipe';

@Component({
    selector: 'language-match-pages',
    standalone: false,
    templateUrl: './language-match-pages.component.html',
    providers: [LanguageDisplayPipe],
})
export class LanguageMatchPagesComponent extends BaseContainerComponentController implements OnInit {
    pages: any = [];
    domainId: any;
    progress: any = null;
    subheaderTranslation: string = '';
    type: any;
    exports: any[] = [];
    tabs: any[] = [];

    languageMatchRepoService = inject(LanguageMatchRepoService);
    translateService = inject(TranslateService);
    override pipeService = inject(PipeService);
    override sessionService = inject(SessionService);
    languageDisplayPipe = inject(LanguageDisplayPipe);

    constructor (
        private stateService: StateService,
    ) {
        super({ table: true, pages: false, labels: true });
        // Initialize currentSort early to match base class expectations
        this.currentSort = { by: 'title', direction: 'desc' };
    }

    ngOnInit () {
        // Initialize currentSort first to prevent expression changed errors
        this.currentSort = { by: 'title', direction: 'desc' };

        this.search = '';
        this.pages = [];
        this.domainId = this.stateService.params.domainId;
        this.progress = null;
        this.subheaderTranslation = '';

        // Initialize language filter properties manually
        this.filters.languages = [];
        this.availableFilters.languages = [];
        this.getLanguages();

        this.exports = [
            {
                name: this.translateService.getString('Pages with incorrect language tag'),
                data: {
                    category: 'pages_with_language_issues',
                },
            },
        ];

        this.tabs = [
            {
                name: this.translateService.getString('Pages with incorrect language'),
                faIcon: 'fas fa-exclamation-triangle',
                value: 'incorrect_language',
            },
            {
                name: this.translateService.getString('Pages missing language'),
                faIcon: 'fas fa-tags',
                value: 'missing_language',
            },
        ];
        this.activeTab = 'incorrect_language';

        this.getPage();
    }

    get isLanguageAttributeEnabled (): boolean {
        return this.sessionService.domain?.scan?.use_lang_attribute === true;
    }

    override getPage (): void {
        const params: any = {
            page: this.page,
            page_size: this.pageSize,
            search: this.search,
            type: this.type,
            sort_by: this.currentSort.by,
            sort_dir: this.currentSort.direction,
        };

        params.tags = ['mismatched_language'];

        // Set has_declared_language based on active tab
        if (this.activeTab === 'incorrect_language') {
            params.has_declared_language = true;
        } else if (this.activeTab === 'missing_language') {
            params.has_declared_language = false;
        }

        const updatedParams = this.updateParamsWithFilters(params);

        this.progress = this.languageMatchRepoService.pages(updatedParams).then((data: any) => {
            this.pages = data;
            this.getSubHeaderDescription();
        }, () => {});
    }

    getSubHeaderDescription () {
        this.subheaderTranslation = this.translateService.getPlural(
            this.pipeService.get('monNumber')((this.pages as any).total || 0, '0,0'),
            '1 page',
            '[[count]] pages',
            { count: (this.pages as any).total || 0 },
            (this.pages as any).total || 0,
        );
    }

    onFiltersChanges (changes: any) {
        this.updateFilters(changes);
        this.getPage();
    }

    // Use the standard base class method for sorting
    // No need to override onMonTableSort - it's already implemented in the base class

    // Base class already provides onPageChange, onPerPageChange, onStatus, onSearch, getLabels, updateLabels methods

    // Override the filter parameters to use detected_languages instead of languages
    override updateParamsWithFilters (params: any): any {
        // Handle labels filter from base class
        if (this.selectedLabels && this.selectedLabels.length > 0) {
            params['labels[]'] = this.selectedLabels;
        }

        // Override language filter to use detected_languages for language match
        if (this.selectedLanguages && this.selectedLanguages.length > 0) {
            params['detected_languages[]'] = this.selectedLanguages;
        }

        return params;
    }

    // Override getLanguages to fetch only detected languages from language match summary
    override getLanguages (): void {
        // Fetch the detected languages asynchronously
        this.languageMatchRepoService.summary({}).then((data) => {
            if (data?.detected_languages) {
                this.availableFilters.languages = Object.keys(data.detected_languages).map((language) => {
                    return {
                        name: this.pipeService.get('translateLanguage')(language),
                        lang: language,
                    };
                });
            } else {
                this.availableFilters.languages = [];
            }
        }, () => {
            this.availableFilters.languages = [];
        });
    }

    // Helper method to get tooltip text for remaining detected languages
    getRemainingLanguagesTooltip (languages: string[]): string {
        if (!languages || languages.length <= 2) {
            return '';
        }
        const remaining = languages.slice(2);
        return remaining.map(lang => this.languageDisplayPipe.transform(lang)).join(', ');
    }
}
